import idb from 'idb';

const KEY_DB_NAME = 'KeyDB';
const KEY_DB_VERSION = 1;
const KEY_OBJ_STORE = 'keys';

const SIGN_ALGORITHM = {
    name: 'RSA-PSS',
    hash: 'SHA-256',
    saltLength: 32
};
const KW_ALGORITHM = {
    name: 'AES-KW',
    length: 256
};

export class CryptoUtils {

    static putKey(userId, jwk, secret) {
        // Create a wrapping key from secret
        const wrappingKeyPromise = this._importWrappingKey(secret);

        // Import the key itself in JWK format
        const importKeyPromise = this._importJWK(jwk);

        return Promise.all([wrappingKeyPromise, importKeyPromise])
            .then(keys => {
                // Encrypt the key for storage
                const wrapKeyPromise = crypto.subtle.wrapKey(
                    'jwk',
                    keys[1],
                    keys[0],
                    KW_ALGORITHM
                );
                return Promise.all([keys[1], wrapKeyPromise]);
            })
            .then(keys => {
                // Store the key in the DB
                return this._runInDB('readwrite', keyStore => {
                    keyStore.put({userId: userId, key: keys[1]});
                    return Promise.resolve(keys[0]);
                });
            });
    }

    static getKey(userId, secret) {
        // Create a wrapping key from secret
        return this._importWrappingKey(secret)
            .then(wrappingKey => {
                return this._runInDB('readonly', keyStore => {
                    // Get the encrypted key
                    return keyStore.get(userId)
                        .then(data => {
                            if (!data) {
                                return null;
                            }

                            // Decrypt the key for usage
                            return crypto.subtle.unwrapKey(
                                'jwk',
                                data.key,
                                wrappingKey,
                                KW_ALGORITHM,
                                SIGN_ALGORITHM,
                                false,
                                ['sign']
                            );
                        });
                });
            });
    }

    static hasKey(userId) {
        return this._runInDB('readonly', keyStore => {
            return keyStore.get(userId).then(data => !!data);
        });
    }

    static signStringWithJWK(jwk, str) {
        return this._importJWK(jwk).then(key => this.signString(key, str));
    }

    static signString(key, str) {
        return crypto.subtle.sign(
            SIGN_ALGORITHM,
            key,
            this._getStringEncoder()(str).buffer
        ).then(signature => {
            return this._arrayBufferToBase64(signature);
        })
    }

    static signAccreditation(key, obj) {
        if (!obj.verifiedIdentity) {
            obj.verifiedIdentity = {};
        }

        const vidSignPromise = crypto.subtle.sign(
            SIGN_ALGORITHM,
            key,
            this._verifiedIdentityToArray(obj.verifiedIdentity).buffer
        ).then(signature => {
            obj.verifiedIdentity.signature = this._arrayBufferToBase64(signature);
        });

        const accSignPromise = crypto.subtle.sign(
            SIGN_ALGORITHM,
            key,
            this._accreditationToArray(obj).buffer
        ).then(signature => {
            obj.signature = this._arrayBufferToBase64(signature);
        });

        return Promise.all([vidSignPromise, accSignPromise]).then(() => obj);
    }

    static _runInDB(trType, callback) {
        return idb.open(
            KEY_DB_NAME,
            KEY_DB_VERSION,
            upgradeDB => {
                const keyStore = upgradeDB.createObjectStore(
                    KEY_OBJ_STORE,
                    {keyPath: 'userId'}
                );
                keyStore.createIndex('UserIndex', 'userId', {unique: true});
            }
        ).then(db => {
            const tx = db.transaction(KEY_OBJ_STORE, trType);
            const keyStore = tx.objectStore(KEY_OBJ_STORE);

            return callback(keyStore)
                .then(result => {
                    return Promise.all([result, tx.complete]);
                }).then(values => {
                    db.close();
                    return values[0];
                });
        });
    }

    static _importJWK(jwk) {
        return crypto.subtle.importKey(
            'jwk',
            jwk,
            SIGN_ALGORITHM,
            true,
            ['sign']
        );
    }

    static _importWrappingKey(secret) {
        const secretBytes = this._base64ToArrayBuffer(secret);
        return crypto.subtle.importKey(
            'raw',
            secretBytes,
            KW_ALGORITHM,
            false,
            ['wrapKey', 'unwrapKey']
        )
    }

    static _verifiedIdentityToArray(vid) {
        const enc = this._getStringEncoder();
        const parts = [
            'countryCode',
            'nationalId',
            'firstName',
            'lastName'
        ].map(k => enc(vid[k]));
        return this._buildArrayFromParts(parts);
    }

    static _accreditationToArray(accreditationRequest) {
        const enc = this._getStringEncoder();

        const vid = accreditationRequest.verifiedIdentity || {};
        const acc = accreditationRequest.accreditationData || {};

        const vidPrefix = this._verifiedIdentityToArray(vid);

        const basicAccParts = [
            'level',
            'issuedAt'
        ].map(k => enc(acc[k]));

        const titles = acc.titles || {};
        let titleParts = ['original', titles.original];
        const titleKeys = Object.keys(titles);
        titleKeys.sort();
        titleKeys.forEach(k => {
            if (k !== 'original') {
                titleParts.push(k);
                titleParts.push(titles[k]);
            }
        });
        titleParts = titleParts.map(enc);

        const accParts = this._buildArrayFromParts(
            basicAccParts.concat(titleParts)
        );

        return this._concatUInt8Arrays(vidPrefix, accParts);
    }

    static _getStringEncoder() {
        const enc = new TextEncoder('utf-8');
        return s => enc.encode(s || '');
    }

    static _concatUInt8Arrays(a, b) {
        const array = new Uint8Array(a.length + b.length);
        array.set(a, 0);
        array.set(b, a.length);
        return array;
    }

    static _buildArrayFromParts(parts) {
        const totalPartSize = parts.reduce((acc, x) => acc + x.byteLength, 0);
        // parts.length is for 0xFF separators
        const totalSize = totalPartSize + parts.length;
        const array = new Uint8Array(totalSize);

        let offset = 0;
        parts.forEach(x => {
            array.set(x, offset);
            offset += x.byteLength;
            array[offset] = 0xFF;
            ++offset;
        });

        return array;
    }

    static _base64ToArrayBuffer(base64) {
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; ++i) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }

    static _arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; ++i) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
}



// WEBPACK FOOTER //
// ./src/utils/cryptoUtils.js